import { cloneDeep, get, has, isEqual, isPlainObject, set, startCase } from 'lodash';
import { currencyUnitsToCurrencySymbolMap } from '@pdqdev/pdq-shared/dist/utils/currency-utils';
import {
  ChangeEntry,
  ChangeKind,
  ChangeReviewConfig,
  LEVEL_RANK,
  ReviewCurrencies,
  ValueFormatter,
} from './change-review.model';

const EMPTY_TEXT = 'Empty';
const DEFAULT_SECTION = 'General';

type RawChange = Pick<ChangeEntry, 'segments' | 'path' | 'pattern' | 'kind' | 'before' | 'after'>;

export function diffChanges(before: unknown, after: unknown, config: ChangeReviewConfig): ChangeEntry[] {
  const normalize = config.normalize ?? ((value: unknown) => value);
  const raw: RawChange[] = [];
  walk(normalize(before, 'before'), normalize(after, 'after'), [], config, raw);

  // sort is stable, so form order is kept within a level
  return raw.map((change) => toEntry(change, config)).sort((a, b) => LEVEL_RANK[a.level] - LEVEL_RANK[b.level]);
}

export function matchesPattern(pattern: string, target: string): boolean {
  return pattern === target || pattern.startsWith(`${target}.`) || pattern.startsWith(`${target}[`);
}

export function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  return isPlainObject(value) && Object.keys(value as object).length === 0;
}

export function formatValue(value: unknown): string {
  if (isEmptyValue(value)) return EMPTY_TEXT;
  if (typeof value === 'boolean') return value ? 'On' : 'Off';
  if (Array.isArray(value)) return value.map(formatValue).join(', ');
  if (isPlainObject(value)) {
    return Object.entries(value as Record<string, unknown>)
      .map(([key, nested]) => [key, formatValue(nested)])
      .filter(([, text]) => text && text !== EMPTY_TEXT)
      .map(([key, text]) => `${startCase(key)}: ${text}`)
      .join(' · ');
  }
  return String(value);
}

export function moneyFormatter(currencies: ReviewCurrencies): ValueFormatter {
  return (value, side) => {
    const currency = side === 'before' ? currencies.before : currencies.after();
    const symbol = currencyUnitsToCurrencySymbolMap[currency];
    return symbol ? `${symbol}${Number(value)}` : `${Number(value)}${currency ? ` ${currency}` : ''}`;
  };
}

// a newer save wins, except for fields the merchant changed; entityPaths = payload path → entity path
export function rebaseOnLatestSave<T extends object>(
  savedPayload: T,
  openedEntity: object,
  latestEntity: object,
  entityPaths: Record<string, string> = {},
  // server lists carry their own ids, so callers can compare the way their review does
  isSame: (path: string, a: unknown, b: unknown) => boolean = (_, a, b) => sameValue(a, b),
): T {
  const rebased = cloneDeep(savedPayload);
  const paths = [...Object.keys(rebased).map((key) => [key, key]), ...Object.entries(entityPaths)];
  paths.forEach(([payloadPath, entityPath]) => {
    if (!has(rebased, payloadPath) || !has(latestEntity, entityPath)) return;
    const opened = get(openedEntity, entityPath);
    // a field the payload shapes differently from the server can't be swapped in safely
    const sameShape = isSame(payloadPath, opened, get(rebased, payloadPath));
    if (sameShape && !isSame(payloadPath, opened, get(latestEntity, entityPath))) {
      set(rebased, payloadPath, cloneDeep(get(latestEntity, entityPath)));
    }
  });
  return rebased;
}

function sameValue(a: unknown, b: unknown): boolean {
  if (isEmptyValue(a) && isEmptyValue(b)) return true;
  if (typeof a !== 'object' && typeof b !== 'object') return String(a) === String(b);
  return isEqual(a, b);
}

// segments are path parts as the form sees them: keys, `[n]` for a list item, `[]` for a set member
function walk(before: unknown, after: unknown, segments: string[], config: ChangeReviewConfig, out: RawChange[]): void {
  const pattern = toPath(segments.map(asPattern));
  if (pattern && config.ignore?.some((ignored) => matchesPattern(pattern, ignored))) return;
  if (isEmptyValue(before) && isEmptyValue(after)) return;

  if (isPlainObject(before) || isPlainObject(after)) {
    if (isContainer(before, isPlainObject) && isContainer(after, isPlainObject)) {
      const beforeObject = (before ?? {}) as Record<string, unknown>;
      const afterObject = (after ?? {}) as Record<string, unknown>;
      new Set([...Object.keys(beforeObject), ...Object.keys(afterObject)]).forEach((key) =>
        walk(beforeObject[key], afterObject[key], [...segments, key], config, out),
      );
      return;
    }
  }

  if (Array.isArray(before) || Array.isArray(after)) {
    if (isContainer(before, Array.isArray) && isContainer(after, Array.isArray)) {
      walkArray((before ?? []) as unknown[], (after ?? []) as unknown[], segments, config, out);
      return;
    }
  }

  if (isEqual(before, after)) return;
  const kind = isEmptyValue(before) ? ChangeKind.Added : isEmptyValue(after) ? ChangeKind.Removed : ChangeKind.Changed;
  out.push(rawChange(segments, kind, before, after));
}

function isContainer(value: unknown, guard: (value: unknown) => boolean): boolean {
  return guard(value) || value === null || value === undefined || value === '';
}

// lists of objects pair up by position; lists of plain values compare as sets, so reordering isn't a change
function walkArray(
  before: unknown[],
  after: unknown[],
  segments: string[],
  config: ChangeReviewConfig,
  out: RawChange[],
): void {
  if ([...before, ...after].every((item) => !isPlainObject(item) && !Array.isArray(item))) {
    const member = [...segments, '[]'];
    before
      .filter((item) => !after.some((candidate) => isEqual(candidate, item)))
      .forEach((item) => out.push(rawChange(member, ChangeKind.Removed, item, undefined)));
    after
      .filter((item) => !before.some((candidate) => isEqual(candidate, item)))
      .forEach((item) => out.push(rawChange(member, ChangeKind.Added, undefined, item)));
    return;
  }

  for (let index = 0; index < Math.max(before.length, after.length); index++) {
    const item = [...segments, `[${index}]`];
    if (index >= after.length) out.push(rawChange(item, ChangeKind.Removed, before[index], undefined));
    else if (index >= before.length) out.push(rawChange(item, ChangeKind.Added, undefined, after[index]));
    else walk(before[index], after[index], item, config, out);
  }
}

function rawChange(segments: string[], kind: ChangeKind, before: unknown, after: unknown): RawChange {
  return { segments, path: toPath(segments), pattern: toPath(segments.map(asPattern)), kind, before, after };
}

const asPattern = (segment: string) => (segment.startsWith('[') ? '[]' : segment);

const toPath = (segments: string[]) =>
  segments.reduce((path, segment) => (segment.startsWith('[') || !path ? path + segment : `${path}.${segment}`), '');

function toEntry(change: RawChange, config: ChangeReviewConfig): ChangeEntry {
  const formatter = config.format?.[change.pattern] ?? formatValue;
  return {
    ...change,
    beforeText: isEmptyValue(change.before) ? EMPTY_TEXT : formatter(change.before, 'before'),
    afterText: isEmptyValue(change.after) ? EMPTY_TEXT : formatter(change.after, 'after'),
    label: buildLabel(change.segments, config.labels ?? {}),
    listLabel: buildLabel(change.segments.map(asPattern), config.labels ?? {}),
    section: mostSpecific(change.pattern, config.sections ?? {}) ?? DEFAULT_SECTION,
    level: mostSpecific(change.pattern, config.levels ?? {}) ?? 'light',
  };
}

function mostSpecific<T>(pattern: string, byPattern: Record<string, T>): T | undefined {
  const match = Object.keys(byPattern)
    .filter((key) => matchesPattern(pattern, key))
    .sort((a, b) => b.length - a.length)[0];
  return match === undefined ? undefined : byPattern[match];
}

function buildLabel(segments: string[], labels: Record<string, string>): string {
  const parts: string[] = [];
  let prefix = '';

  segments.forEach((token) => {
    if (token.startsWith('[')) {
      prefix += '[]';
      const label = labels[prefix];
      const position = token.length > 2 ? ` ${Number(token.slice(1, -1)) + 1}` : '';
      if (label === '') return;
      if (label) {
        parts.push(`${label}${position}`);
      } else if (position && parts.length) {
        parts[parts.length - 1] += position;
      }
      return;
    }

    prefix = joinPath(prefix, token);
    const label = labels[prefix] ?? startCase(token);
    if (label) parts.push(label);
  });

  return parts.join(' › ');
}

function joinPath(path: string, key: string): string {
  return path ? `${path}.${key}` : key;
}
